import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  MessageSquare, Send, Phone, MapPin, Clock, User, Check, CheckCheck,
  Loader2, AlertCircle, X, Minimize2, Maximize2, Image, MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { taxiChat } from '@/lib/api';
import { toast } from 'sonner';
import { format, isToday, isYesterday, formatDistanceToNow } from 'date-fns';
import { de, tr, enUS } from 'date-fns/locale';

const getDateLocale = (lang) => {
  switch (lang) {
    case 'de': return de;
    case 'tr': return tr;
    default: return enUS;
  }
};

const formatMessageTime = (dateString, lang) => {
  const date = new Date(dateString);
  const locale = getDateLocale(lang);
  
  if (isToday(date)) {
    return format(date, 'HH:mm', { locale });
  } else if (isYesterday(date)) {
    return `${lang === 'de' ? 'Gestern' : lang === 'tr' ? 'Dün' : 'Yesterday'} ${format(date, 'HH:mm', { locale })}`;
  }
  return format(date, 'dd.MM HH:mm', { locale });
};

// Single Message Component
const ChatMessage = ({ message, isOwn, lang }) => {
  const isSystem = message.message_type === 'system';
  
  if (isSystem) {
    return (
      <div className="flex justify-center my-3">
        <span className="text-xs text-muted-foreground bg-gray-100 px-3 py-1 rounded-full">
          {message.content}
        </span>
      </div>
    );
  }
  
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[80%] ${isOwn ? 'order-2' : 'order-1'}`}>
        {!isOwn && message.sender_name && (
          <p className="text-xs text-muted-foreground mb-1 ml-2">{message.sender_name}</p>
        )}
        <div
          className={`px-4 py-2 rounded-2xl ${
            isOwn 
              ? 'bg-orange-500 text-white rounded-br-none' 
              : 'bg-gray-100 text-gray-900 rounded-bl-none'
          }`}
        >
          {message.message_type === 'location' ? (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{message.content}</span>
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
          )}
        </div>
        <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end mr-1' : 'ml-2'}`}>
          <span className="text-xs text-muted-foreground">
            {formatMessageTime(message.created_at, lang)}
          </span>
          {isOwn && (
            message.read 
              ? <CheckCheck className="w-3 h-3 text-blue-500" />
              : <Check className="w-3 h-3 text-gray-400" />
          )}
        </div>
      </div>
    </div>
  );
};

// Chat Window Component (can be embedded or modal)
const TaxiChatWindow = ({
  conversationId,
  bookingId,
  participantId,
  participantName,
  bookingNumber,
  currentUserId,
  isModal = false,
  isOpen = true,
  onClose,
  onMinimize,
  isMinimized = false
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [actualConversationId, setActualConversationId] = useState(conversationId);
  const [wsConnected, setWsConnected] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Initialize or fetch conversation
  const initializeConversation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let convId = conversationId;
      
      // If no conversation ID, create one
      if (!convId && bookingId && participantId) {
        const response = await taxiChat.createConversation(bookingId, participantId);
        convId = response.data.conversation?.id;
        setActualConversationId(convId);
      }
      
      if (convId) {
        const response = await taxiChat.getConversation(convId);
        setConversation(response.data.conversation);
        setMessages(response.data.messages || []);
        setActualConversationId(convId);
      }
    } catch (err) {
      console.error('Error initializing conversation:', err);
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [conversationId, bookingId, participantId, t]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!actualConversationId) return;
    
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    try {
      const wsUrl = taxiChat.getChatWsUrl(actualConversationId);
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Chat WebSocket connected');
        setWsConnected(true);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.id) {
            setMessages(prev => {
              // Prevent duplicates
              if (prev.find(m => m.id === data.id)) return prev;
              return [...prev, data];
            });
            scrollToBottom();
          }
        } catch (e) {
          console.error('Error parsing chat message:', e);
        }
      };
      
      wsRef.current.onerror = () => {
        setWsConnected(false);
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      };
    } catch (err) {
      console.error('Error connecting chat WebSocket:', err);
    }
  }, [actualConversationId, scrollToBottom]);

  // Initialize
  useEffect(() => {
    if (isOpen && !isMinimized) {
      initializeConversation();
    }
  }, [isOpen, isMinimized, initializeConversation]);

  // Connect WebSocket when conversation is ready
  useEffect(() => {
    if (actualConversationId && isOpen && !isMinimized) {
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [actualConversationId, isOpen, isMinimized, connectWebSocket]);

  // Scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage.trim() || !actualConversationId || sending) return;
    
    const messageContent = newMessage.trim();
    setNewMessage('');
    
    try {
      setSending(true);
      
      // Optimistic update
      const tempMessage = {
        id: `temp-${Date.now()}`,
        conversation_id: actualConversationId,
        sender_id: currentUserId,
        content: messageContent,
        message_type: 'text',
        read: false,
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, tempMessage]);
      scrollToBottom();
      
      // Send to server
      const response = await taxiChat.sendMessage(actualConversationId, messageContent);
      
      // Replace temp message with real one
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? response.data.message : m
      ));
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error(t('taxi.messageSendFailed', 'Failed to send message'));
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => !m.id.startsWith('temp-')));
      setNewMessage(messageContent);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Chat content
  const chatContent = (
    <div className="flex flex-col h-full" data-testid="taxi-chat-window">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-orange-500 text-white rounded-t-lg">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8 bg-orange-400">
            <AvatarFallback className="text-white text-sm">
              {(participantName || 'U').charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">{participantName || t('taxi.driver', 'Driver')}</p>
            {bookingNumber && (
              <p className="text-xs opacity-80">#{bookingNumber}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {wsConnected && (
            <span className="w-2 h-2 bg-green-400 rounded-full" title={t('taxi.connected', 'Connected')} />
          )}
          {onMinimize && (
            <Button variant="ghost" size="icon" onClick={onMinimize} className="h-8 w-8 text-white hover:bg-orange-600">
              <Minimize2 className="w-4 h-4" />
            </Button>
          )}
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-white hover:bg-orange-600">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-3">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <AlertCircle className="w-10 h-10 text-red-500 mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={initializeConversation} className="mt-2">
              {t('common.retry', 'Retry')}
            </Button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <MessageSquare className="w-10 h-10 text-gray-300 mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('taxi.noMessages', 'No messages yet. Start the conversation!')}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((message, index) => (
              <ChatMessage
                key={message.id || index}
                message={message}
                isOwn={message.sender_id === currentUserId}
                lang={lang}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-3 border-t bg-gray-50">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('taxi.typeMessage', 'Type a message...')}
            disabled={loading || sending}
            className="flex-1"
            data-testid="chat-input"
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={!newMessage.trim() || loading || sending}
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="chat-send-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );

  // Return based on modal or embedded
  if (isModal) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md p-0 gap-0 h-[500px]">
          {chatContent}
        </DialogContent>
      </Dialog>
    );
  }

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 cursor-pointer"
        onClick={onMinimize}
      >
        <div className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-full shadow-lg hover:bg-orange-600 transition-colors">
          <MessageSquare className="w-5 h-5" />
          <span className="font-medium">{participantName || t('taxi.chat', 'Chat')}</span>
          <Maximize2 className="w-4 h-4" />
        </div>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden h-[400px]">
      {chatContent}
    </Card>
  );
};

// Floating Chat Button with Chat Window
export const FloatingTaxiChat = ({
  conversationId,
  bookingId,
  participantId,
  participantName,
  bookingNumber,
  currentUserId
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const response = await taxiChat.getUnreadCount();
        setUnreadCount(response.data.unread_count || 0);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    };
    
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!isOpen && !isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full bg-orange-500 hover:bg-orange-600 shadow-lg"
          data-testid="floating-chat-btn"
        >
          <MessageSquare className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <TaxiChatWindow
        conversationId={conversationId}
        bookingId={bookingId}
        participantId={participantId}
        participantName={participantName}
        bookingNumber={bookingNumber}
        currentUserId={currentUserId}
        isOpen={true}
        isMinimized={true}
        onMinimize={() => setIsMinimized(false)}
        onClose={() => {
          setIsMinimized(false);
          setIsOpen(false);
        }}
      />
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 shadow-2xl rounded-lg overflow-hidden">
      <TaxiChatWindow
        conversationId={conversationId}
        bookingId={bookingId}
        participantId={participantId}
        participantName={participantName}
        bookingNumber={bookingNumber}
        currentUserId={currentUserId}
        isOpen={isOpen}
        onMinimize={() => setIsMinimized(true)}
        onClose={() => setIsOpen(false)}
      />
    </div>
  );
};

export default TaxiChatWindow;
